// Every Pro organization's plan fit (D109, APS-361), by Enterprise score and then the projected
// bill: who to offer Enterprise, and whose margin to review. Each row opens the organization's plan
// fit, where every figure shows how it was computed. Console D's list (APS-412, admin-d.md).
import { PageHead, Section } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { type PlanFit, planFitsQuery } from '../api/queries'
import { DataTable } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { useFormat } from '../lib/format'
import { VERDICT_LABEL, verdictWord } from './PlanFit'

type Sort = 'score' | 'bill12' | 'margin' | 'captured'

const COLUMNS: { sort: Sort; label: MessageId }[] = [
  { sort: 'score', label: 'planFits.col.score' },
  { sort: 'captured', label: 'planFits.col.captured' },
  { sort: 'bill12', label: 'planFits.col.bill12' },
  { sort: 'margin', label: 'planFits.col.margin' },
]

const value = (x: PlanFit, sort: Sort): number =>
  sort === 'score'
    ? x.fit.score * 1e12 + x.fit.bill12
    : sort === 'bill12'
      ? x.fit.bill12
      : sort === 'captured'
        ? x.fit.captured
        : (x.fit.margin_share ?? Number.NEGATIVE_INFINITY)

export function PlanFitsPage() {
  const { t } = useI18n()
  const { usd, pct1, n } = useFormat()
  const q = useQuery(planFitsQuery)
  const [sort, setSort] = useState<{ by: Sort; desc: boolean }>({ by: 'score', desc: true })
  const rows = [...(q.data ?? [])].sort((a, b) => {
    const d = value(a, sort.by) - value(b, sort.by)
    return sort.desc ? -d : d
  })
  const version = q.data?.[0]?.version
  const head = (c: (typeof COLUMNS)[number], last = false) => (
    <th
      key={c.sort}
      scope="col"
      className={`num sort${sort.by === c.sort ? ' on' : ''}${last ? ' last' : ''}`}
      aria-sort={sort.by === c.sort ? (sort.desc ? 'descending' : 'ascending') : 'none'}
    >
      <button
        type="button"
        onClick={() =>
          setSort(
            sort.by === c.sort ? { by: c.sort, desc: !sort.desc } : { by: c.sort, desc: true },
          )
        }
      >
        {t(c.label)}
      </button>
    </th>
  )
  return (
    <main id="main" className="pg">
      <PageHead
        title={t('planFits.title')}
        meta={
          version === undefined || !q.isSuccess
            ? t('planFits.metaEmpty')
            : t('planFits.meta', { n: rows.length, version })
        }
        actions={
          <Link className="btn sm" to="/quote">
            {t('planFits.newQuote')}
          </Link>
        }
      />
      <Section title={t('planFits.list')} meta={t('planFits.listMeta')}>
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} retry={() => q.refetch()} />
        ) : rows.length === 0 ? (
          <Empty title={t('planFits.empty')} />
        ) : (
          <DataTable label={t('table.of', { name: t('planFits.list') })} minWidth={680}>
            <thead>
              <tr>
                <th className="f" scope="col">
                  {t('planFits.col.org')}
                </th>
                <th scope="col">{t('planFits.col.verdict')}</th>
                {COLUMNS.map((c, i) => head(c, i === COLUMNS.length - 1))}
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => {
                const word = verdictWord(t, x.fit)
                return (
                  <tr key={x.org_id}>
                    <td className="f wrap">
                      <Link
                        to="/orgs/$orgId"
                        params={{ orgId: x.org_id }}
                        hash="plan-fit"
                        className="ul"
                      >
                        {x.name}
                      </Link>
                    </td>
                    <td className="wrap">
                      <span className="money-verdict">{t(VERDICT_LABEL[x.fit.verdict])}</span>
                      <span className={word.tone ? `t2 c-${word.tone}` : 't2'}>{word.d}</span>
                    </td>
                    <td className="num">{n(x.fit.score)}</td>
                    <td className="num">{n(x.fit.captured)}</td>
                    <td className="num">{usd(x.fit.bill12, 0)}</td>
                    <td className="num last">
                      {x.fit.margin_share === null ? (
                        '–'
                      ) : x.fit.verdict === 'review_price' ? (
                        // Under the floor: the one figure on the row in the crash tone.
                        <span className="c-crash">{pct1(x.fit.margin_share)}</span>
                      ) : (
                        pct1(x.fit.margin_share)
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </DataTable>
        )}
      </Section>
    </main>
  )
}
